import React from 'react';
import { formatCurrency, formatDate, getStatusColor, getInitials } from '../../../lib/utils';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../../ui/alert-dialog';
import {
  Play, XCircle, Loader2, Building2, DoorOpen, Calendar, Pencil, Lock,
  Archive, History, Shield, Download, Trash2, AlertTriangle
} from 'lucide-react';

const renderFeesBadges = (lease) => {
  const fees = lease.additional_fees;
  if (!fees) return null;

  const feeItems = [];
  if (fees.internet > 0) feeItems.push(`Internet: ${formatCurrency(fees.internet)}`);
  if (fees.water > 0) feeItems.push(`Water: ${formatCurrency(fees.water)}`);
  if (fees.electricity > 0) feeItems.push(`Electricity: ${formatCurrency(fees.electricity)}`);
  if (fees.appliances > 0) feeItems.push(`Appliances: ${formatCurrency(fees.appliances)}`);
  if (fees.laundry > 0) feeItems.push(`Laundry: ${formatCurrency(fees.laundry)}`);
  if (fees.other1_amount > 0) feeItems.push(`${fees.other1_name || 'Other'}: ${formatCurrency(fees.other1_amount)}`);
  if (fees.other2_amount > 0) feeItems.push(`${fees.other2_name || 'Other'}: ${formatCurrency(fees.other2_amount)}`);

  if (feeItems.length === 0) return null;

  return (
    <div className="text-xs text-muted-foreground mt-1">
      + {feeItems.join(', ')}
    </div>
  );
};

export const LeaseCard = ({
  lease, index, deleting,
  onEdit, onActivate, onForceActivate, onRenew, onHistory, onDownloadPdf, onTerminate, onDelete
}) => (
  <Card className="animate-fade-in" style={{ animationDelay: `${index * 0.03}s` }}>
    <CardContent className="p-6">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Tenant Info */}
        <div className="flex items-center gap-3 lg:w-1/5">
          <Avatar className="w-12 h-12">
            <AvatarFallback className="bg-[#6B8E6B] text-white">
              {getInitials(lease.tenant?.name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-semibold">{lease.tenant?.name || 'Unknown'}</p>
            <p className="text-sm text-muted-foreground">{lease.tenant?.email}</p>
          </div>
        </div>

        {/* Property & Unit */}
        <div className="lg:w-1/5">
          <div className="flex items-center gap-2 text-sm">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            {lease.property?.name}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <DoorOpen className="w-4 h-4" />
            Room {lease.unit?.unit_number}
          </div>
        </div>

        {/* Dates & Rent */}
        <div className="lg:w-1/5">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            {formatDate(lease.start_date)} - {formatDate(lease.end_date)}
          </div>
          <p className="text-lg font-semibold text-[#C85A42] mt-1">
            {formatCurrency(lease.total_monthly_rent || lease.monthly_rent)}/mo
          </p>
          {renderFeesBadges(lease)}
        </div>

        {/* Status */}
        <div className="lg:w-1/5">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Badge className={getStatusColor(lease.status)}>{lease.status}</Badge>
              {lease.is_locked && (
                <Lock className="w-4 h-4 text-muted-foreground" title="Locked" />
              )}
            </div>
            {lease.status === 'pending' && !lease.tenant_acknowledged && (
              <span className="text-xs text-amber-600">Awaiting acknowledgement</span>
            )}
            {lease.status === 'pending' && lease.tenant_acknowledged && (
              <span className="text-xs text-emerald-600">✓ Acknowledged - Ready to activate</span>
            )}
            {lease.previous_lease_id && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <History className="w-3 h-3" /> Renewed
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 lg:w-1/5 lg:justify-end flex-wrap">
          {lease.status === 'pending' && !lease.is_locked && !lease.tenant_acknowledged && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(lease)}
              data-testid={`edit-lease-${lease.lease_id}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
          )}

          {lease.status === 'pending' && lease.tenant_acknowledged && (
            <Button
              size="sm"
              onClick={() => onActivate(lease.lease_id)}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid={`activate-lease-${lease.lease_id}`}
            >
              <Play className="w-4 h-4 mr-1" />
              Activate
            </Button>
          )}

          {['pending', 'draft', 'pending_acknowledgment'].includes(lease.status) && !(lease.status === 'pending' && lease.tenant_acknowledged) && (
            <Button
              size="sm"
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              onClick={() => onForceActivate(lease.lease_id)}
              data-testid={`force-activate-lease-${lease.lease_id}`}
            >
              <Shield className="w-4 h-4 mr-1" />
              Force Activate
            </Button>
          )}

          {(lease.status === 'active' || (lease.status === 'pending' && lease.is_locked)) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRenew(lease)}
              data-testid={`renew-lease-${lease.lease_id}`}
            >
              <Archive className="w-4 h-4 mr-1" />
              Issue New
            </Button>
          )}

          <Button
            size="sm"
            variant="outline"
            onClick={() => onHistory(lease.lease_id)}
            data-testid={`history-lease-${lease.lease_id}`}
          >
            <History className="w-4 h-4 mr-1" />
            History
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => onDownloadPdf(lease.lease_id)}
            data-testid={`download-pdf-${lease.lease_id}`}
          >
            <Download className="w-4 h-4 mr-1" />
            PDF
          </Button>

          {lease.status === 'active' && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => onTerminate(lease.lease_id)}
              data-testid={`terminate-lease-${lease.lease_id}`}
            >
              <XCircle className="w-4 h-4" />
            </Button>
          )}

          {(lease.status === 'pending' || lease.status === 'draft' || lease.status === 'active') && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  disabled={deleting === lease.lease_id}
                  data-testid={`delete-lease-${lease.lease_id}`}
                >
                  {deleting === lease.lease_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                    Delete Lease?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this lease for <strong>{lease.tenant?.name}</strong>?
                  </AlertDialogDescription>
                  {lease.status === 'active' && (
                    <p className="text-sm text-amber-600 dark:text-amber-500">
                      Note: Active leases with payment history cannot be deleted. Use "Terminate" instead.
                    </p>
                  )}
                  <ul className="list-disc list-inside text-sm space-y-1 ml-2 text-muted-foreground">
                    <li>The unit will be released back to vacant</li>
                    <li>The tenant will be notified</li>
                    <li>This action cannot be undone</li>
                  </ul>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(lease.lease_id)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);
